import React, { useEffect, useRef, useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import { useDispatch } from 'react-redux'
import Button from '../../../components/Button'
import CheckboxBlock from '../../../legacy/components/CheckboxBlock'
import { getApplicationModelName } from './fns'
import { convertUserInputToDisplayFormat } from './tableHeaders'

const useJobEditor = (output, input, deleteAlertModalProps, updateJobIndex) => {
  const { jobs } = output

  const dispatch = useDispatch()
  const [newIndexValue, setNewIndexValue] = useState('')
  const handleDeleteJobRowClick = (value) => {
    setNewIndexValue(value)
    deleteAlertModalProps.openModal()
  }
  const handleFooterSubmitClick = () => {
    dispatch({ type: 'JOB_REMOVE', payload: { removeJobIndex: newIndexValue } })
  }

  const handleJobDeleteClick = (row) => {
    handleDeleteJobRowClick(row.row.index)
  }

  const handleJobUpdateClick = (row) => {
    dispatch({ type: 'JOB_UPDATE_INIT', payload: { updateJobIndex: row.row.index } })
  }

  const jobEditor = (row) => {
    return (
      <div className="">
        {updateJobIndex !== row.row.index ? (
          <Button
            variant="text"
            className="button dowelingActionButton"
            onClick={(e) => {
              handleJobUpdateClick(row)
            }}
          >
            <span className="button__text">Edit</span>
          </Button>
        ) : null}

        {jobs.length > 1 ? (
          <Button
            variant="text"
            className="button dowelingActionButton"
            onClick={(e) => {
              handleJobDeleteClick(row)
            }}
          >
            <span className="button__text">Delete</span>
          </Button>
        ) : null}
      </div>
    )
  }

  // highlight the job row for edit/update
  const rowEditProps = (row) => {
    return row.index === updateJobIndex
      ? {
          className: 'active',
        }
      : {}
  }

  const EditableCell = ({
    value: initialValue,
    row: { index },
    column: { id },
    updateMyData, // This is a custom function that we supplied to our table instance
  }) => {
    if (index === updateJobIndex) {
      // We need to keep and update the state of the cell normally
      const [value, setValue] = useState(initialValue)
      const myRef = useRef(null)

      const onChange = (e) => {
        setValue(e.target.value)
      }

      // We'll only update the external data when the input is blurred
      const onBlur = () => {
        updateMyData(jobs, index, id, value)
        myRef.current.classList.remove('-focused')
      }

      const onFocus = (e) => {
        myRef.current.classList.add('-focused')
      }
      // If the initialValue is changed external, sync it up with our state
      useEffect(() => {
        setValue(initialValue)
      }, [initialValue])

      return (
        <div ref={myRef} className="formGroup">
          <div className="inputGroup">
            <input
              className="jobName"
              maxLength="100"
              tabIndex="0"
              type="text"
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
            />
          </div>
        </div>
      )
    } else {
      return (
        <div className="formGroup">
          <div className="inputGroup readOnly">
            <input
              className="jobName"
              maxLength="100"
              readOnly="readonly"
              type="text"
              value={initialValue}
              onChange={null}
            />
          </div>
        </div>
      )
    }
  }

  // When our cell renderer calls updateMyData, we'll use
  // the rowIndex, columnId and new value to update the
  // original data
  const updateMyData = (src, rowIndex, columnId, value) => {
    const oldJobs = src // calculation.output.jobs
    const jobs = oldJobs.map((row, index) => {
      if (index === rowIndex) {
        return Object.assign({}, oldJobs[rowIndex], { [columnId]: value })
      }
      return row
    })

    dispatch({ type: 'JOB_REPLACE', payload: jobs })
  }

  const handleOrderChange = (name, value) => {
    dispatch({ type: 'FIELD_UPDATE_CALCULATION', payload: { adhesiveAnchorModel: name } })
  }

  const OrderCheckbox = (props) => {
    return (
      <CheckboxBlock
        name={props.model}
        value={props.model === input.adhesiveAnchorModel}
        onChange={handleOrderChange}
      />
    )
  }

  const getJobColumns = (models, jobs) => {
    const dynamicColumns = models.reduce((result, item) => {
      const temp = {
        id: item.model,
        Header: ReactHtmlParser(getApplicationModelName(item)),
        accessor: (d) => {
          return d[item.model] && d[item.model].toFixed(2)
        },
        Footer: (row) => {
          const x = jobs.reduce((total, entry) => total + entry[item.model], 0)
          return (
            <>
              {input.source === 'EC' ? <OrderCheckbox {...item} /> : null}
              {x.toFixed(2)}
            </>
          )
        },
      }
      result.push(temp)
      return result
    }, [])

    const jobColumns = [
      {
        Header: 'Action',
        accessor: '',
        Cell: (row) => jobEditor(row),
        Footer: 'Total',
      },
      {
        Header: 'Job',
        accessor: 'jobName',
        Cell: EditableCell,
      },
    ]
      .concat(dynamicColumns)
      .concat([
        {
          Header: 'User Inputs',
          accessor: (d) => {
            return convertUserInputToDisplayFormat(d.userInputs)
          },
          Cell: (row) => <div className="prettyPrint">{ReactHtmlParser(row.cell.value)}</div>,
        },
      ])

    return jobColumns
  }

  return {
    getJobColumns,
    handleFooterSubmitClick,
    updateMyData,
  }
}

export default useJobEditor
